import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle, Shield, Factory, Beaker } from "lucide-react";
import { Button } from "../components/ui/button";
import { SectionHeading } from "../components/shared/SectionHeading";
import { siteContent, images } from "../data/content";

export const Quality = () => {
  return (
    <div>
      {/* Hero */}
      <section className="py-16 md:py-24 bg-charcoal-paper border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <span className="font-mono text-coconut text-sm tracking-wider uppercase mb-2 block">
              Quality Assurance
            </span>
            <h1
              className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold uppercase tracking-tight text-white mb-6"
              data-testid="quality-title"
            >
              Quality & Factory
            </h1>
            <p className="text-lg text-zinc-400">
              Our commitment to consistency and quality has made us a trusted partner for importers worldwide since 2016.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Trust Highlights */}
      <section className="py-24 bg-charcoal">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-charcoal-paper border border-white/5 p-8"
            >
              <Shield className="w-12 h-12 text-coconut mb-4" />
              <h3 className="font-heading text-white uppercase text-xl mb-2">Factory Audited</h3>
              <p className="text-zinc-400">
                Our facility has been audited by Carsurin, ensuring our production processes meet international standards.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-charcoal-paper border border-white/5 p-8"
            >
              <Factory className="w-12 h-12 text-coconut mb-4" />
              <h3 className="font-heading text-white uppercase text-xl mb-2">Established 2016</h3>
              <p className="text-zinc-400">
                Years of experience in manufacturing and exporting coconut shell charcoal briquettes to global markets.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="bg-charcoal-paper border border-white/5 p-8"
            >
              <Beaker className="w-12 h-12 text-coconut mb-4" />
              <h3 className="font-heading text-white uppercase text-xl mb-2">Rigorous Testing</h3>
              <p className="text-zinc-400">
                Every batch undergoes quality control testing for moisture, ash content, and burn performance.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Carsurin Certificate */}
      <section className="py-24 bg-charcoal">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="font-mono text-coconut text-sm tracking-wider uppercase mb-2 block">
                Lab Tested
              </span>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold uppercase tracking-tight text-white mb-6">
                Certificate of Analysis
              </h2>
              <p className="text-zinc-400 mb-6">
                Our products are tested by PT. Carsurin Tbk, a laboratory accredited by KAN (National Accreditation Committee - Indonesia) for ISO 17025:2017.
              </p>
              <div className="space-y-3 text-zinc-300">
                <div className="flex justify-between border-b border-white/10 pb-2">
                  <span>Moisture</span>
                  <span className="text-coconut font-mono">5.01%</span>
                </div>
                <div className="flex justify-between border-b border-white/10 pb-2">
                  <span>Ash Content</span>
                  <span className="text-coconut font-mono">2.10%</span>
                </div>
                <div className="flex justify-between border-b border-white/10 pb-2">
                  <span>Volatile Matter</span>
                  <span className="text-coconut font-mono">16.41%</span>
                </div>
                <div className="flex justify-between border-b border-white/10 pb-2">
                  <span>Fixed Carbon</span>
                  <span className="text-coconut font-mono">76.48%</span>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-charcoal-paper border border-white/5 p-4"
            >
              <img 
                src={images.carsurinCertificate} 
                alt="Carsurin Certificate of Analysis" 
                className="w-full h-auto"
                data-testid="carsurin-certificate"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* QC Checklist */}
      <section className="py-24 bg-charcoal-paper">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="font-mono text-coconut text-sm tracking-wider uppercase mb-2 block">
                Quality Control
              </span>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold uppercase tracking-tight text-white mb-6">
                QC Checklist
              </h2>
              <p className="text-zinc-400 mb-8">
                Every production batch goes through our comprehensive quality control process before shipping.
              </p>
              <div className="space-y-4" data-testid="qc-checklist">
                {siteContent.qcChecklist.map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-start gap-4 bg-charcoal border border-white/5 p-4"
                  >
                    <CheckCircle className="w-5 h-5 text-coconut flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-heading text-white uppercase text-sm">{item.item}</h4>
                      <p className="text-zinc-500 text-sm">{item.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="grid grid-cols-2 gap-4"
            >
              <div className="aspect-square bg-charcoal border border-white/5 overflow-hidden col-span-2">
                <img src={images.charcoalGlow} alt="Quality charcoal briquettes" className="w-full h-full object-cover" />
              </div>
              <div className="aspect-square bg-charcoal border border-white/5 overflow-hidden col-span-2">
                <img src={images.hookah} alt="Hookah usage" className="w-full h-full object-cover" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Factory Gallery */}
      <section className="py-24 bg-charcoal noise-overlay">
        <div className="max-w-7xl mx-auto px-4 md:px-8 relative z-10">
          <SectionHeading
            title="Our Products"
            subtitle="Premium coconut shell charcoal briquettes for shisha"
          />

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {[
              { img: images.charcoalGlow, label: "Glowing Briquettes" },
              { img: images.hookah, label: "In Use" },
              { img: images.charcoalGlow, label: "Quality Product" },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="relative aspect-square bg-charcoal-paper border border-white/5 overflow-hidden group"
              >
                <img
                  src={item.img}
                  alt={item.label}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-charcoal to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <span className="font-heading text-white uppercase text-sm">{item.label}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-charcoal-paper">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold uppercase tracking-tight text-white mb-6">
            Want to Know More?
          </h2>
          <p className="text-zinc-400 mb-8 max-w-2xl mx-auto">
            Contact us to discuss our quality standards, request test certificates, or arrange a virtual factory tour.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button
              asChild
              size="lg"
              className="bg-coconut text-charcoal hover:bg-coconut-hover font-heading uppercase"
              data-testid="quality-quote-btn"
            >
              <Link to="/contact">
                Contact Us
                <ArrowRight className="ml-2 w-4 h-4" />
              </Link>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="border-white/20 text-white hover:bg-white/5 font-heading uppercase"
            >
              <a
                href={`${siteContent.contact.whatsappLink}?text=${encodeURIComponent("Hi, I'd like to know more about your quality standards and factory.")}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                WhatsApp Us
              </a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};
